import React, { useState, useEffect, useMemo, useRef } from 'react';

const DEFAULT_SUBTITLES = [
    'Creatives',
    'Musicians',
    'Artists',
    'Beatmakers',
    'Rappers',
    'DJs',
    'Teachers',
    'Bands',
    'Designers',
    'Dancers',
    'Guitarists',
    'Drummers',
    'Vocalists',
    'Friends',
    'Neighbors',
    'Fans',
    'Songwriters',
    'Photographers',
    'Videographers',
];

interface RotatingSubtitleProps {
    subtitles?: string[];
    minDuration?: number;
    maxDuration?: number;
    isLoading?: boolean;
}

// Fisher-Yates shuffle algorithm
const shuffleArray = (array: string[]) => {
    if (!array.length) return [];
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
};

const RotatingSubtitle = ({
    subtitles = DEFAULT_SUBTITLES,
    minDuration = 2000,
    maxDuration = 4000,
    isLoading = false
}: RotatingSubtitleProps) => {
    // Memoize shuffled subtitles to prevent unnecessary re-shuffling
    const shuffledSubtitles = useMemo(() => {
        return shuffleArray(subtitles.length ? subtitles : DEFAULT_SUBTITLES);
    }, [subtitles]);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const [isInViewport, setIsInViewport] = useState(true);
    const timeoutRef = useRef<{ timeout: NodeJS.Timeout; isLoading: boolean } | null>(null);
    const slowdownFactorRef = useRef(1);
    const loadCountRef = useRef(-1);
    const containerRef = useRef<HTMLSpanElement>(null);

    // Update slowdown factor when loading starts
    useEffect(() => {
        if (isLoading) {
            loadCountRef.current += 1;
            // Use power function for more aggressive slowdown
            // This means:
            // Load 1: 1.5x slower
            // Load 2: 2.4x slower
            // Load 3: 3.6x slower
            // Load 4: 5.0x slower
            // etc.
            slowdownFactorRef.current = loadCountRef.current > 0
                ? 1 + Math.pow(loadCountRef.current, 1.4) * 0.3
                : 1;
        }
    }, [isLoading]);

    // Set up intersection observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsInViewport(entry.isIntersecting);
            },
            {
                root: null,
                rootMargin: '0px',
                threshold: 0.1
            }
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            if (containerRef.current) {
                observer.unobserve(containerRef.current);
            }
        };
    }, []);

    // Effect to handle the transition timing
    useEffect(() => {
        const scheduleNextTransition = () => {
            // Clear any existing timeout
            if (timeoutRef.current?.timeout) {
                clearTimeout(timeoutRef.current.timeout);
            }

            // Don't schedule next transition if not in viewport
            if (!isInViewport) return;

            // Base timing values
            const baseLoadingTime = 1000; // 1 second during loading
            const baseMinTime = 2000;
            const baseMaxTime = 4000;

            // Use fixed 1 second during loading, otherwise random between 2 and 4 seconds
            // Apply progressive slowdown factor only when not loading
            const baseInterval = isLoading
                ? baseLoadingTime
                : Math.floor(Math.random() * (baseMaxTime - baseMinTime) + baseMinTime);

            const interval = isLoading ? baseInterval : baseInterval * slowdownFactorRef.current;

            const timeout = setTimeout(() => {
                setIsVisible(false);
                setTimeout(() => {
                    // Get the next index, ensuring it's not the same as the current one
                    setCurrentIndex((prev) => {
                        let nextIndex = (prev + 1) % shuffledSubtitles.length;
                        // If we're about to show the same subtitle, skip to the next one
                        if (shuffledSubtitles[nextIndex] === shuffledSubtitles[prev]) {
                            nextIndex = (nextIndex + 1) % shuffledSubtitles.length;
                        }
                        return nextIndex;
                    });
                    setIsVisible(true);
                    // Only schedule next transition if we're still in the same loading state and in viewport
                    if (isLoading === timeoutRef.current?.isLoading && isInViewport) {
                        scheduleNextTransition();
                    }
                }, 300); // Shorter delay for snappier animation
            }, interval);

            // Store the current loading state with the timeout
            timeoutRef.current = { timeout, isLoading };
        };

        // Reset and schedule new transition when loading state changes or viewport visibility changes
        if (timeoutRef.current?.timeout) {
            clearTimeout(timeoutRef.current.timeout);
        }
        scheduleNextTransition();

        return () => {
            if (timeoutRef.current?.timeout) {
                clearTimeout(timeoutRef.current.timeout);
            }
        };
    }, [isLoading, shuffledSubtitles, isInViewport]);

    // Handle empty subtitles array
    if (!shuffledSubtitles.length) {
        return (
            <span className="inline-block ml-2 text-gray-500">
                Profiles
            </span>
        );
    }

    return (
        <span ref={containerRef} className="inline-block ml-2">
            <noscript className="text-gray-500">Profiles</noscript>
            <span className={`inline-block transition-all duration-300 ease-in-out ${isVisible ? 'opacity-75 translate-y-0 text-indigo-500' : 'opacity-0 -translate-y-4 text-gray-500'}`}>
                {shuffledSubtitles[currentIndex]}
            </span>
        </span>
    );
};

export default RotatingSubtitle;
